/**
 * Adhan synchronization data per reciter.
 *
 * Each reciter has different timings for adhan lines based on their
 * unique recitation style, pace, and pauses.
 *
 * This allows the fullscreen overlay to display the correct line at
 * the right moment, synchronized with the actual audio playback
 * (using audio.currentTime).
 *
 * Structure: { reciterId: 'timeline', forFajr: { ...timeline } }
 */

// Default/generic timeline (~110s for standard adhan, ~130s for Fajr)
const DEFAULT_TIMELINE = [
  { ar: 'اللَّهُ أَكْبَرُ، اللَّهُ أَكْبَرُ', fr: 'Allah est le plus grand, Allah est le plus grand', transliteration: 'Allāhu akbar, Allāhu akbar', at: 0, duration: 9000 },
  { ar: 'اللَّهُ أَكْبَرُ، اللَّهُ أَكْبَرُ', fr: 'Allah est le plus grand, Allah est le plus grand', transliteration: 'Allāhu akbar, Allāhu akbar', at: 9000, duration: 9000 },
  { ar: 'أَشْهَدُ أَنْ لَا إِلٰهَ إِلَّا اللَّهُ', fr: 'J\'atteste qu\'il n\'y a de divinité que Dieu', transliteration: 'Ashhadu an lā ilāha illā-llāh', at: 18000, duration: 9000 },
  { ar: 'أَشْهَدُ أَنْ لَا إِلٰهَ إِلَّا اللَّهُ', fr: 'J\'atteste qu\'il n\'y a de divinité que Dieu', transliteration: 'Ashhadu an lā ilāha illā-llāh', at: 27000, duration: 9000 },
  { ar: 'أَشْهَدُ أَنَّ مُحَمَّدًا رَسُولُ اللَّهِ', fr: 'J\'atteste que Muḥammad est le messager de Dieu', transliteration: 'Ashhadu anna Muḥammadan rasūlu-llāh', at: 36000, duration: 10000 },
  { ar: 'أَشْهَدُ أَنَّ مُحَمَّدًا رَسُولُ اللَّهِ', fr: 'J\'atteste que Muḥammad est le messager de Dieu', transliteration: 'Ashhadu anna Muḥammadan rasūlu-llāh', at: 46000, duration: 10000 },
  { ar: 'حَيَّ عَلَى الصَّلَاةِ', fr: 'Venez à la prière', transliteration: 'Ḥayya ʿalā ṣ-ṣalāh', at: 56000, duration: 8000 },
  { ar: 'حَيَّ عَلَى الصَّلَاةِ', fr: 'Venez à la prière', transliteration: 'Ḥayya ʿalā ṣ-ṣalāh', at: 64000, duration: 8000 },
  { ar: 'حَيَّ عَلَى الْفَلَاحِ', fr: 'Venez à la félicité', transliteration: 'Ḥayya ʿalā l-falāḥ', at: 72000, duration: 8000 },
  { ar: 'حَيَّ عَلَى الْفَلَاحِ', fr: 'Venez à la félicité', transliteration: 'Ḥayya ʿalā l-falāḥ', at: 80000, duration: 8000 },
  { ar: 'اللَّهُ أَكْبَرُ، اللَّهُ أَكْبَرُ', fr: 'Allah est le plus grand, Allah est le plus grand', transliteration: 'Allāhu akbar, Allāhu akbar', at: 88000, duration: 9000 },
  { ar: 'لَا إِلٰهَ إِلَّا اللَّهُ', fr: 'Il n\'y a de divinité que Dieu', transliteration: 'Lā ilāha illā-llāh', at: 97000, duration: 8000 },
];

const FAJR_EXTRA = [
  { ar: 'الصَّلَاةُ خَيْرٌ مِنَ النَّوْمِ', fr: 'La prière est meilleure que le sommeil', transliteration: 'Aṣ-ṣalātu khayrun mina n-nawm', at: 88000, duration: 9000 },
  { ar: 'الصَّلَاةُ خَيْرٌ مِنَ النَّوْمِ', fr: 'La prière est meilleure que le sommeil', transliteration: 'Aṣ-ṣalātu khayrun mina n-nawm', at: 97000, duration: 9000 },
  { ar: 'اللَّهُ أَكْبَرُ، اللَّهُ أَكْبَرُ', fr: 'Allah est le plus grand, Allah est le plus grand', transliteration: 'Allāhu akbar, Allāhu akbar', at: 106000, duration: 9000 },
  { ar: 'لَا إِلٰهَ إِلَّا اللَّهُ', fr: 'Il n\'y a de divinité que Dieu', transliteration: 'Lā ilāha illā-llāh', at: 115000, duration: 8000 },
];

// Map soundId patterns to reciter name (for future use)
const RECITER_PATTERNS = {
  'abdel_basset': 'Abdel Basset',
  'abdul_basit': 'Abdul Basit',
  'ahmed_al_ajmi': 'Ahmed Al-Ajmi',
  'mishary': 'Mishary Alafasy',
  'default': 'Default',
};

/**
 * Get the adhan timeline for a specific reciter.
 * Falls back to default if reciter not found.
 *
 * @param {string} reciterId - Sound ID or reciter identifier
 * @param {boolean} forFajr - Whether this is the Fajr adhan (includes extra line)
 * @returns {Array} Array of timeline entries with { ar, fr, transliteration, at, duration }
 */
export function getAdhanSyncTimeline(reciterId = 'default', forFajr = false) {
  // Normalize reciterId
  const id = (reciterId || '').toLowerCase().replace(/\s+/g, '_');
  
  // For now, return default timeline for all (future: add custom timelines per reciter)
  let timeline = [...DEFAULT_TIMELINE];
  
  if (forFajr) {
    // Insert Fajr extra lines before closing takbir
    const baseDuration = DEFAULT_TIMELINE[DEFAULT_TIMELINE.length - 2]?.at || 80000;
    const updatedTimeline = DEFAULT_TIMELINE.slice(0, -2);
    timeline = [
      ...updatedTimeline,
      ...FAJR_EXTRA,
    ];
  }
  
  return timeline;
}

/**
 * Find line index at a specific time (in milliseconds).
 * @param {Array} timeline - Adhan timeline
 * @param {number} audioTime - Current audio time in seconds (audio.currentTime)
 * @returns {number} Index of the line to display, or -1 if none
 */
export function getLineAtTime(timeline, audioTime) {
  if (!timeline?.length) return -1;
  const ms = audioTime * 1000; // Convert seconds to ms
  
  // Find last line where ms >= at
  for (let i = timeline.length - 1; i >= 0; i -= 1) {
    if (ms >= timeline[i].at) return i;
  }
  return -1;
}

/**
 * Get total duration of adhan in seconds.
 * @param {boolean} forFajr
 * @returns {number} Duration in seconds
 */
export function getAdhanDuration(forFajr = false) {
  const timeline = getAdhanSyncTimeline('default', forFajr);
  if (!timeline.length) return 0;
  const last = timeline[timeline.length - 1];
  return (last.at + last.duration) / 1000;
}

export { RECITER_PATTERNS };
